/* eslint-disable react/prop-types */
import React, { Component, useEffect, useState } from 'react';
import { Popconfirm, Button, Popover, message } from 'antd';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createSelector, createStructuredSelector } from 'reselect';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import inRange from 'lodash/inRange';
import omit from 'lodash/omit';
import intersection from 'lodash/intersection';
import toUpper from 'lodash/toUpper';
import moment from 'moment';
import config from '$config';
import { api } from '$app/api';
import Loading from '$app/shared/components/Loading';
import injectSaga from '$app/utils/injectSaga';
import injectReducer from '$app/utils/injectReducer';
import reducer from './reducer';
import saga from './saga';
import { poundsToPence } from '$app/utils/util';
import { getCustomerLabourRatePence } from './util';
import GarageInfo from '$app/shared/components/GarageInfo/GarageInfo';
import {
  BOOKING_STATUS,
  GARAGE_STATUS,
  INVOICE_STATUS,
} from '$app/apps/bookings/containers/BookingPage/constants';
import {
  COMPLETE_LIMIT,
  ADJUSTMENT_TYPE,
  COLLECTION_AND_DELIVERY_TYPE,
  VEHICLE_WEIGHT_KG,
  DISCOUNT_TYPE,
  COUPON_TYPE,
} from './constants';
import {
  getBookingSelector,
  quoteSelector,
  editingKeySelector,
  garageSelector,
  getSelectedGaragePrice,
  jobInvitationSelector,
  garageWorkOperationPricesSelector,
  originalWorkOperationPricesSelector,
  savingKeySelector,
  deletingKeySelector,
  garageBillStatusSelector,
  getVehicle,
  getSendQuoteLoading,
} from './selectors';
import quoteWorkOperationsSelector, {
  approvedQuoteSelector,
  approvedQuoteTotalsSelector,
  proposedQuoteSelector,
  proposedQuoteTotalsSelector,
  hasFinalisedProposedQuote as hasFinalisedProposedQuoteSelector,
  prepareTableData,
} from './selectors/quoteWorkOperationsSelector';
import collectionDeliverySelector from './selectors/collectionDeliverySelector';
import campaignSelector from './selectors/campaignSelector';
import creditSelector from './selectors/creditSelector';
import adjustmentSelector, { feeSelector } from './selectors/adjustmentSelector';

const additionalDiscountsSelector = () => createSelector(
  quoteSelector(),
  (quote) => {
    if (!quote || !quote.lineItems) return [];
    const items = quote.lineItems.filter(li => li.type === 'additional-discount');
    return prepareTableData(items);
  }
);
import {
  deleteLineItemAction,
  saveWorkOperationAction,
  deleteWorkOperationAction,
  addDraftWo,
  openPopupAction,
  sendQuoteToCustomer as sendQuoteToCustomerAction,
  getGarageWorkOperationPrices,
  getOriginalWorkOperationPrices,
  approveProposedWorkOperation,
  declineProposedWorkOperation,
  finaliseProposedWorkOperations,
  sendJobInvite,
  updateToCustomerReview as updateToCustomerReviewAction,
} from './actions';
import { patchBookingAction } from '../actions';
import WorkDetailsTable from './components/WorkDetailsTable';
import {
  WorkDetailsContainer,
  NoteComponent,
  StyledSubmitButton,
  SendDraftFooter,
  StyledAlert,
} from './styled';
import Header from './components/Header';
import WorkOperationEditorModal from './components/WorkOperationEditor/WorkOperationEditorModal';
import AdjustmentOperationModal from './components/WorkOperationEditor/AdjustmentModal';
import GarageCAndDModal from './components/WorkOperationEditor/editGarageCAndDModal';
import DriverTipModal from './components/WorkOperationEditor/editDriverTipModal';
import CampaignModal from './components/WorkOperationEditor/editingCampaignModal';

import { INVITE_STATUS } from '../constants';

/**
 * Return the list of prices
 * Can be used to override prices eg:
 *  - if a booking has a service and the selected garage has a negociated price for MOT & service,
 *    we need to use that price for MOT instead of the standard price
 * @param {*} garageWorkOperationPrices list of all prices
 * @param {*} quote booking quote
 * @param {*} garagePrice garage operations prices
 */
const getAutocompleteList = (garageWorkOperationPrices, quote, garagePrice) => {
  const motWithServicePrice = get(garagePrice, 'motWithFullMajorService');
  const lineItems = get(quote, 'lineItems', []);
  const prices = get(garageWorkOperationPrices, 'list', []);
  if (motWithServicePrice && lineItems.find(({ sku }) => ['full-service', 'major-service'].includes(sku))) {
    const mot = prices.find(({ sku }) => sku === 'mot');
    if (!mot) return prices;
    mot.garageCost = motWithServicePrice;
    mot.garageCostWithVat = motWithServicePrice;
    mot.subLineItems.map((sli) => {
      if (sli.type === 'labour') {
        sli.garageCost = motWithServicePrice;
        sli.garageCostWithVat = motWithServicePrice;
        sli.labourCost = poundsToPence(motWithServicePrice);
        sli.price = motWithServicePrice;
      }
      return sli;
    });
  }

  return prices;
};

const hasCustomerNotes = ({ notes, collectionNote: driverNote }) => {
  const mechanicNote = notes.find(({ noteType }) => noteType === 'customer-to-mechanic');
  if (driverNote || mechanicNote) return true;
  return false;
};

const GarageLoader = ({ garageId, children }) => {
  const [garage, setGarage] = useState(null);
  const [loading, setLoading] = useState(Boolean(garageId));
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadGarage = async () => {
      if (!garageId) {
        setGarage(null);
        setLoading(false);
        setHasError(false);
        return;
      }

      setLoading(true);
      setHasError(false);

      try {
        const { data } = await api.get(`/garages/${garageId}`);
        if (!isMounted) return;
        setGarage(data);
      } catch (error) {
        if (!isMounted) return;
        setHasError(true);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadGarage();

    return () => {
      isMounted = false;
    };
  }, [garageId]);

  if (loading) return <Loading />;
  if (hasError && garageId) return 'Garage labour rate is missing';

  return children(garage);
};

const SendQuoteButton = ({ disabled, onSendQuote }) => {
  if (disabled) {
    return (
      <Popover
        className="pull-right"
        content={
          <p style={{ margin: 0, maxWidth: 280 }}>
            Please approve or decline all additional operations below
          </p>
        }
        placement="top"
      >
        <Button style={{ margin: '10px' }} type="primary" disabled>
          Send quote to garage
        </Button>
      </Popover>
    );
  }
  return (
    <Popconfirm
      className="pull-right"
      title="Confirm send quote?"
      placement="topLeft"
      onConfirm={onSendQuote}
      okText="Yes"
      cancelText="No"
    >
      <Button style={{ margin: '10px' }} type="primary">
        Send quote to garage
      </Button>
    </Popconfirm>
  );
};

class WorkDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      note: '',
      newAdjustmentRow: false,
      editableAdjustment: null,
      savingAdjustment: false,
      deletingAdjustment: false,
      /**
       * Will open work operation editor when set to a line item
       */
      editWorkOperation: null,
      editingGarageCAndD: false,
      editableGarageCAndDItem: null,
      savingGarageCAndD: false,
      editingDriverTip: false,
      editableDriverTipItem: null,
      savingDriverTip: false,
    };
  }

  componentDidMount = () => {
    this.setNotes();
    this.setProposedWorkStatuses();
  };

  componentDidUpdate = (prevProps) => {
    const { booking } = this.props;

    if (prevProps.booking.proposedWorkId !== booking.proposedWorkId) this.setProposedWorkStatuses();
  };

  setNotes = () => {
    const vehicleWeight = get(this.props, 'vehicle.weight', 0);
    const vehicleWeightIssue = vehicleWeight > VEHICLE_WEIGHT_KG;

    const activeInviteNote = this.props.jobInvitation?.notes;
    const draftInviteNote = this.props.booking?.garageInvites?.find((i) => i.status === 'draft')?.notes;
    const bookingNotes = this.props.booking?.notes?.find(
      (n) => n.noteType === 'customer-to-mechanic'
    )?.content;

    const weightNote = 'This vehicle weighs between 3000kg and 3500kg so may require a more powerful ramp.';

    let existingNote = activeInviteNote || draftInviteNote || bookingNotes;

    if (vehicleWeightIssue) {
      existingNote = `${weightNote} ${existingNote}`;
    }

    if (existingNote) {
      this.setState({ note: existingNote });
    }
  };

  setProposedWorkStatuses = () => {
    const { proposedQuoteItems } = this.props;

    if (!isEmpty(proposedQuoteItems)) {
      proposedQuoteItems.map((pqi) => {
        if (pqi.workStatus === 'approved') this.props.approveProposedWorkOperation(pqi);
        if (pqi.workStatus === 'declined') this.props.declineProposedWorkOperation(pqi);
      });
    }
  };

  openKis = () => {
    const { vrm } = this.props.booking.vehicle;
    const win = window.open(`http://www.kis-unipart.co.uk/KIS/DocSearch.aspx?Type=Doc&Veh=${vrm}`, '_blank');
    win.focus();
  };

  getWorkOperationFormError() {
    const { quote, editingKey } = this.props;
    const excludePremiumMot = ({ source }) =>
      ['MOT-PREMIUM', 'FIXED-FIXTER-DRIVER-FEE', 'FIXED-GARAGE-DRIVER-FEE'].indexOf(source) === -1;
    const excludeFree = ({ sku }) =>
      [
        'free-timing-belt-replacement-check',
        'fluid-check',
        'exhaust-check',
        'steering-check',
        'tyre-check',
        'suspension-check',
        'battery-check',
        'brakes-check',
        'air-con-check',
        'timing-belt-check',
      ].indexOf(sku) === -1;

    const zeroLineItem = quote.lineItems
      .filter((li) => li.showGarage)
      .filter(excludePremiumMot)
      .filter(excludeFree)
      .find((li) => !(li.garageCostWithVat > 0) || !(li.garageCost > 0));

    if (editingKey) {
      return 'Please save your open work operation before sending invite';
    }
    if (zeroLineItem) {
      return `${
        zeroLineItem.workSummary || zeroLineItem.title
      } should have a garage price to send job invitation`;
    }
    return null;
  }

  getGarageInvoiceStatus = () => {
    const { garageBills } = this.props.booking;
    if (!garageBills.length) return null;
    return get(
      garageBills.find((gb) => trim(gb.status) === 'active' && gb.type === 'bill'),
      'invoiceStatus',
      null
    );
  };

  get isGarageInvoiceOnHold() {
    const { masterStatusId } = this.props.booking;
    const invoiceStatus = this.getGarageInvoiceStatus();
    return (
      invoiceStatus === INVOICE_STATUS.ON_HOLD &&
      (inRange(masterStatusId, BOOKING_STATUS.WORK_COMPLETE, BOOKING_STATUS.CLOSED + 1) ||
        masterStatusId === BOOKING_STATUS.UNPAID)
    );
  }

  get canDoAdjustment() {
    const { masterStatusId, garageBills } = this.props.booking;
    if (!garageBills.length) return masterStatusId === BOOKING_STATUS.AT_GARAGE;
    return this.isGarageInvoiceOnHold;
  }

  get labourRate() {
    return get(this.props, 'garagePrice.labourRate', 0);
  }

  setDefaultSavingDeletingStates = () => {
    this.setState({
      savingAdjustment: false,
      deletingAdjustment: false,
    });
  };

  openWorkOperationEditor = (lineItem = {}) => {
    if (!this.props.garage) {
      return message.warn('Please pick the garage you would like to send the job invitation to');
    }
    if (isEmpty(lineItem.children)) {
      lineItem.children = [
        // set default labour for work operation
        {
          key: 'new-labour', // predefined line items key of a new work operation should start with `new-`
          workSummary: 'Labour',
          type: 'labour',
          quantity: 0,
          price: this.labourRate,
          garageCostWithVat: 0,
          garageCost: 0,
          vatExempt: false,
          order: 1,
          className: 'labour',
        },
      ];
    }
    return this.setState({ editWorkOperation: lineItem });
  };

  handleEditLineItem = (lineItem) => {
    if (lineItem.type === COLLECTION_AND_DELIVERY_TYPE) {
      return this.openGarageCAndDModal(lineItem);
    }

    if (lineItem.sku === 'fixter-driver-fee' || lineItem.sku === 'garage-driver-fee') {
      return this.openDriverTipModal(lineItem);
    }

    if (lineItem.type === ADJUSTMENT_TYPE) {
      return this.openAdjustmentModal(lineItem);
    }

    if (lineItem.type === DISCOUNT_TYPE || lineItem.type === COUPON_TYPE) {
      return this.toggleCampaignModal(lineItem);
    }

    return this.openWorkOperationEditor(lineItem);
  };

  handleCloseWorkOperationEditor = () => this.setState({ editWorkOperation: null });

  handleDeleteWorkOperationEditor = (operationId) => {
    const { quote } = this.props;
    if (quote && quote.lineItems && quote.lineItems.length === 1) {
      return message.error('Last work operation cannot be deleted.');
    }
    const { quoteType } = this.state.editWorkOperation;
    this.handleCloseWorkOperationEditor();
    this.props.deleteWorkOperation(operationId, quoteType);
  };

  handleSaveWorkOperationEditor = (data) => {
    this.handleCloseWorkOperationEditor();
    this.props.saveWorkOperation(data);
  };

  openAdjustmentModal = (adjustment) => {
    this.setDefaultSavingDeletingStates();

    if (adjustment) {
      this.setState({ newAdjustmentRow: true, editableAdjustment: adjustment });
    } else {
      this.setState({ newAdjustmentRow: true, editableAdjustment: null });
    }
  };

  toggleCampaignModal = (campaign) => {
    if (campaign) {
      this.setState({ editableCampaign: campaign });
    } else {
      this.setState({ editableCampaign: false });
    }
  };

  handleCancelAddAdjustment = () => {
    this.setState({ newAdjustmentRow: false, editableAdjustment: null });
  };

  handleDeleteAdjustment = (operationId) => {
    const { deleteWorkOperation } = this.props;

    deleteWorkOperation(operationId);
    this.handleCancelAddAdjustment();
    this.setState({ deletingAdjustment: true });
  };

  handleAddAdjustment = (adjustment) => {
    if (adjustment.subLineItems) {
      adjustment.subLineItems = adjustment.subLineItems.map((item) => ({
        workSummary: item.workSummary,
        type: item.type || adjustment.type,
        fixedCost: 0,
        fixedCostWithVAT: 0,
        labourTime: 1,
        labourCost: poundsToPence(item.garageCost),
        garageCost: poundsToPence(item.garageCost),
        garageCostWithVat: poundsToPence(item.garageCostWithVat),
        vatExempt: item.vatExempt,
        order: item.order,
        status: 'active',
        operationId: item.operationId,
        accountCode: '303',
        accountCodePurchases: item.accountCodePurchases,
      }));
    }

    /**
     * top level item is disregarded
     * as we roll up the subLineItems as line items
     * https://www.notion.so/fixter/Garage-adjustments-fd44738027ec49d08f58113d1aaa00fb
     */
    this.props.saveWorkOperation({
      operationId: (this.state.editableAdjustment || []).operationId,
      workSummary: 'Garage adjustment',
      vatExempt: true,
      type: adjustment.type,
      fixedCost: 0,
      fixedCostWithVAT: 0,
      garageCost: adjustment.garageCost,
      garageCostWithVat: adjustment.garageCostWithVat,
      lineItems: adjustment.subLineItems || [],
      order: adjustment.order,
      sku: 'adjustment',
      accountCode: '303',
      // accountCodePurchases: 307,
      status: 'active',
    });

    this.handleCancelAddAdjustment();
    this.setState({ savingAdjustment: true });
  };

  scrollToBottom = () => {
    this.newRow.scrollIntoView({ behavior: 'smooth' });
  };

  getEditableAdjustments = (editableAdjustment, depth = 0) => {
    if (!editableAdjustment) return null;

    return {
      ...editableAdjustment,
      workSummary: editableAdjustment.workSummary || editableAdjustment.title,
      type: ADJUSTMENT_TYPE,
      subLineItems: (editableAdjustment.children || []).map((i) => this.getEditableAdjustments(i, depth + 1)),
    };
  };

  get showProposedQuote() {
    const { booking, approvedQuoteItems, proposedQuoteItems } = this.props;
    /**
     * Show proposed work operations after the customer has approved them
     * At this point the garage status will be WORK_STARTED
     * */
    const hasApprovedDeclinedItem = [...approvedQuoteItems, ...proposedQuoteItems].find((i) =>
      ['approved', 'declined'].includes(i.workStatus)
    );
    /**
     * Show proposed work operations during the following garage statuses
     */
    const isInGarageStatus = [
      GARAGE_STATUS.FIXTER_REVIEW,
      GARAGE_STATUS.CUSTOMER_REVIEW,
      GARAGE_STATUS.CUSTOMER_REVIEWED,
      GARAGE_STATUS.WORK_COMPLETE,
    ].includes(booking.garageStatus);
    return hasApprovedDeclinedItem || isInGarageStatus;
  }

  get isCaseManagerReviewed() {
    const { proposedQuoteItems } = this.props;

    return proposedQuoteItems.reduce(
      (acc, { comments = [] }) => acc && !!comments.find((c) => c.type === 'cm-comment'),
      true
    );
  }

  getEditableLine = (editableOneLineItem) => {
    if (!editableOneLineItem) return null;

    return {
      ...editableOneLineItem,
      workSummary: editableOneLineItem.workSummary || editableOneLineItem.title,
      type: editableOneLineItem.type || ADJUSTMENT_TYPE,
      subLineItems: [],
    };
  };

  openGarageCAndDModal = (LineToEdit) => {
    this.setDefaultSavingDeletingStates();
    this.setState({
      editingGarageCAndD: true,
      editableGarageCAndDItem: LineToEdit,
      savingGarageCAndD: false,
    });
  };

  handleCancelGarageCAndD = () => {
    this.setState({ editingGarageCAndD: false, editableGarageCAndDItem: null, savingGarageCAndD: false });
  };

  handleSaveGarageCAndD = (editedOneLineItem) => {
    this.props.saveWorkOperation({ ...omit(editedOneLineItem, ['className', 'render']), lineItems: [] });
    this.setState({ savingGarageCAndD: true, editingGarageCAndD: false, editableGarageCAndDItem: null });
  };

  openDriverTipModal = (LineToEdit) => {
    this.setDefaultSavingDeletingStates();
    this.setState({ editingDriverTip: true, editableDriverTipItem: LineToEdit, savingDriverTip: false });
  };

  handleCancelDriverTip = () => {
    this.setState({ editingDriverTip: false, editableDriverTipItem: null, savingDriverTip: false });
  };

  handleSaveDriverTip = (editedOneLineItem) => {
    this.props.saveWorkOperation({ ...omit(editedOneLineItem, ['className', 'render']), lineItems: [] });
    this.setState({ savingDriverTip: true, editingDriverTip: false, editableDriverTipItem: null });
  };

  getPopConfirmTitle = () => {
    const { vehicle, workOperations } = this.props;
    const { motStatus, motCompletedDateTime } = vehicle;

    const motCompletedDayDifference = motCompletedDateTime && moment().diff(motCompletedDateTime, 'days');
    const passedMOT = motStatus && toUpper(motStatus) === 'PASSED';
    const failedMOT = motStatus && toUpper(motStatus) === 'FAILED';
    const hasMot = workOperations.some(({ type }) => type === 'mot');

    let title = (
      <>
        Mark the <b>quote for advisories</b> as reviewed and send it to the customer by SMS and email
      </>
    );

    if (hasMot && motCompletedDayDifference < COMPLETE_LIMIT) {
      if (passedMOT)
        title = (
          <>
            Mark the <b>quote for MOT test passed</b> as reviewed and send it to the customer by SMS and email
          </>
        );
      if (failedMOT)
        title = (
          <>
            Mark the <b>quote for MOT test failed</b> as reviewed and send it to the customer by SMS and email
          </>
        );
    }

    return <div style={{ width: 200 }}>{title}</div>;
  };

  handleDeleteCampaign = () => {
    const { key } = this.state.editableCampaign;
    const operationId = key.split('discount-row-')[1];
    this.props.deleteWorkOperation(Number(operationId));
    this.props.patchBooking({ campaignId: null });
    this.setState({ editableCampaign: null });
  };

  render() {
    const {
      quote,
      jobInvitation,
      booking,
      workOperations,
      collectionDeliveryLine,
      adjustmentOperation,
      feeOperation,
      campaignList,
      additionalDiscounts,
      creditList,
      garageWorkOperationPrices,
      originalWorkOperationPrices,
      // garage,
      garagePrice,
      savingKey,
      deletingKey,
      openPopup,
      approvedQuoteItems,
      approvedQuoteTotals,
      proposedQuoteItems,
      proposedQuoteTotals,
      hasFinalisedProposedQuote,
      getGarageBillStatus,
      sendQuoteToCustomer,
      updateToCustomerReview,
      sendQuoteLoading,
    } = this.props;
    if (!quote) return null;
    const { editWorkOperation } = this.state;
    const autocompleteList = getAutocompleteList(garageWorkOperationPrices, quote, garagePrice);
    const onSendQuote = () => this.props.finaliseProposedWorkOperations();
    const disableSendQuote = !hasFinalisedProposedQuote;
    const displayCustomerNotesReminder = hasCustomerNotes(booking);

    const draftGarageInvites = booking.garageInvites.filter(({ status }) => status === INVITE_STATUS.DRAFT);
    const sendDrafInviteQuoteButtonText =
      draftGarageInvites.length > 1 ? (
        <>
          Send quote to <b>{draftGarageInvites.length}</b> garages
        </>
      ) : (
        'Send quote to garage'
      );

    return (
      <GarageLoader garageId={get(jobInvitation, 'garageId')}>
        {(garage) => (
          <WorkDetailsContainer>
              <Header
                booking={booking}
                onAddWorkOperation={() => this.openWorkOperationEditor()}
                onAddCouponCode={() => openPopup(jobInvitation || {})}
                displayAdjustmentButton={this.canDoAdjustment && isEmpty(adjustmentOperation)}
                onAdjustment={() => this.openAdjustmentModal()}
                onOpenKIS={this.openKis}
              >
                Work Details{' '}
                {garage && (
                  <>
                    for <GarageInfo garage={garage} />
                  </>
                )}
              </Header>

              <WorkDetailsTable
                originalWorkOperations={originalWorkOperationPrices}
                approvedQuoteItems={approvedQuoteItems}
                approvedQuoteTotals={approvedQuoteTotals}
                proposedQuoteItems={proposedQuoteItems}
                proposedQuoteTotals={proposedQuoteTotals}
                bookingStatus={booking.masterStatusId}
                canDoAdjustment={this.canDoAdjustment}
                onClickEdit={this.handleEditLineItem}
                onDelete={this.handleDeleteWorkOperation}
                campaigns={campaignList}
                additionalDiscounts={additionalDiscounts}
                credits={creditList}
                collectionDeliveryLine={collectionDeliveryLine}
                adjustmentsLine={adjustmentOperation ? [adjustmentOperation] : []}
                feeOperation={feeOperation}
                showProposedQuote={this.showProposedQuote}
                handleApprove={this.props.approveProposedWorkOperation}
                handleDecline={this.props.declineProposedWorkOperation}
                garageBillStatus={getGarageBillStatus}
                vehicle={this.props.vehicle}
              />

              <div
                ref={(el) => {
                  this.newRow = el;
                }}
              />

              {/* Send Job Invite Footer  */}

              {jobInvitation && jobInvitation.status === 'draft' && (
                <SendDraftFooter className="send-draft-footer">
                  {displayCustomerNotesReminder && (
                    <StyledAlert
                      message={
                        <>
                          <span className="highlight">Reminder:</span> This booking has notes from the
                          customer, to share these notes with the garage, copy and paste the content in the
                          field below.
                        </>
                      }
                      icon={
                        <>
                          <img src="/assets/images/Icon_Info@4x.png" alt="info icon" />
                        </>
                      }
                      type="info"
                      showIcon
                    />
                  )}
                  <NoteComponent
                    placeholder="Message for the garage (to be sent with invitation)"
                    value={this.state.note}
                    onChange={(e) => {
                      this.setState({ note: e.target.value });
                    }}
                  />
                  <StyledSubmitButton
                    type="primary"
                    className={this.getWorkOperationFormError() ? 'light-disabled' : ''}
                    onClick={() => {
                      const { note } = this.state;
                      const validationError = this.getWorkOperationFormError();

                      if (validationError) {
                        message.error(validationError);
                      } else {
                        this.props.sendJobInvite(jobInvitation.invitationId, note);
                      }
                    }}
                    disabled={isEmpty(workOperations)}
                  >
                    {sendDrafInviteQuoteButtonText}
                  </StyledSubmitButton>
                </SendDraftFooter>
              )}

              {/* Send Quote Footer  */}

              {booking.masterStatusId === BOOKING_STATUS.AT_GARAGE &&
                booking.garageStatus === 'FIXTER_REVIEW' && (
                  <SendDraftFooter className="send-quote-footer">
                    <div style={{ display: 'inline-block' }}>
                      <Popconfirm
                        title={
                          <div style={{ width: 200 }}>
                            Are you sure you want to skip sending the quote to customer?
                          </div>
                        }
                        placement="top"
                        onConfirm={() => updateToCustomerReview()}
                        okText="Yes"
                        cancelText="No"
                        disabled={sendQuoteLoading}
                      >
                        <Button disabled={sendQuoteLoading} style={{ marginRight: '10px' }} type="primary">
                          Skip sending to customer
                        </Button>
                      </Popconfirm>

                      {config.enableEWO?.[booking.segment] &&
                        ((this.isCaseManagerReviewed && (
                          <Popconfirm
                            title={this.getPopConfirmTitle()}
                            placement="top"
                            onConfirm={() => sendQuoteToCustomer()}
                            okText="Yes"
                            cancelText="No"
                            disabled={sendQuoteLoading}
                          >
                            <Button
                              disabled={sendQuoteLoading}
                              style={{ marginRight: '10px' }}
                              type="primary"
                            >
                              Send quote to the customer
                            </Button>
                          </Popconfirm>
                        )) || (
                          <Popover content="Please review all additional operations before sending to the customer">
                            <Button disabled style={{ marginRight: '10px' }} type="primary">
                              Send quote to the customer
                            </Button>
                          </Popover>
                        ))}
                    </div>
                  </SendDraftFooter>
                )}

              {booking.masterStatusId === BOOKING_STATUS.AT_GARAGE &&
                ['CUSTOMER_REVIEW', 'CUSTOMER_REVIEWED'].indexOf(booking.garageStatus) >= 0 && (
                  <SendDraftFooter className="send-quote-footer">
                    <SendQuoteButton
                    numberOfDraftInvites={draftGarageInvites.length}
                    garageInvites={booking.garageInvites}
                    disabled={disableSendQuote}
                    onSendQuote={onSendQuote}
                  />
                  </SendDraftFooter>
                )}

              {editWorkOperation && (
                <WorkOperationEditorModal
                  workOperation={editWorkOperation}
                  onClose={this.handleCloseWorkOperationEditor}
                  onDelete={this.handleDeleteWorkOperationEditor}
                  onSave={this.handleSaveWorkOperationEditor}
                  autocompleteList={autocompleteList}
                  isCaseManagerReviewed={this.isCaseManagerReviewed}
                  enabledCustomerPriceEdit={
                    booking.masterStatusId <= BOOKING_STATUS.WORK_COMPLETE ||
                    // Allow editing customer price when an invite is declined before sending another
                    booking.masterStatusId === BOOKING_STATUS.GARAGE_DECLINED
                  }
                  bookingStatus={booking.masterStatusId}
                  disableCommentToCustomer={false}
                  labourRateForCustomerPence={getCustomerLabourRatePence(garage, garagePrice)}
                />
              )}

              {/* Opens adjustment modal */}
              {(this.state.newAdjustmentRow ||
                (savingKey && this.state.savingAdjustment) ||
                (deletingKey && this.state.deletingAdjustment)) && (
                <AdjustmentOperationModal
                  visible
                  onOk={this.handleAddAdjustment}
                  onCancel={this.handleCancelAddAdjustment}
                  onDelete={this.handleDeleteAdjustment}
                  editableAdjustment={this.getEditableAdjustments(this.state.editableAdjustment)}
                  isSaving={savingKey && this.state.savingAdjustment}
                  isDeleting={deletingKey && this.state.deletingAdjustment}
                />
              )}
              {/* Opens modal for editing GarageC&D */}
              {this.state.editableGarageCAndDItem &&
                (this.state.editingGarageCAndD || this.state.savingGarageCAndD) && (
                  <GarageCAndDModal
                    title="Edit Garage C&D price"
                    visible
                    onDelete={(...args) => this.handleSaveGarageCAndD(...args)}
                    onSave={(...args) => this.handleSaveGarageCAndD(...args)}
                    onCancel={this.handleCancelGarageCAndD}
                    editableLineItem={this.getEditableLine(this.state.editableGarageCAndDItem)}
                    isSaving={this.state.savingGarageCAndD}
                  />
                )}

              {/* Opens modal for editing DRIVER TIP */}
              {this.state.editableDriverTipItem &&
                (this.state.editingDriverTip || this.state.savingDriverTip) && (
                  <DriverTipModal
                    title="Edit Driver Tip price"
                    visible
                    onDelete={(...args) => this.handleSaveDriverTip(...args)}
                    onSave={(...args) => this.handleSaveDriverTip(...args)}
                    onCancel={this.handleCancelDriverTip}
                    editableLineItem={this.getEditableLine(this.state.editableDriverTipItem)}
                    isSaving={this.state.savingDriverTip}
                  />
                )}

              {this.state.editableCampaign && (
                <CampaignModal
                  visible
                  onDelete={this.handleDeleteCampaign}
                  onCancel={() => this.toggleCampaignModal(null)}
                  campaign={this.state.editableCampaign}
                />
              )}
            </WorkDetailsContainer>
        )}
      </GarageLoader>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    approveProposedWorkOperation: (lineItem) => dispatch(approveProposedWorkOperation(lineItem)),
    declineProposedWorkOperation: (lineItem) => dispatch(declineProposedWorkOperation(lineItem)),
    finaliseProposedWorkOperations: () => dispatch(finaliseProposedWorkOperations()),
    getGarageWorkOperationPrices: () => dispatch(getGarageWorkOperationPrices()),
    getOriginalWorkOperationPrices: () => dispatch(getOriginalWorkOperationPrices()),
    saveWorkOperation: (data) => dispatch(saveWorkOperationAction(data)),
    deleteWorkOperation: (operationId) => dispatch(deleteWorkOperationAction(operationId)),
    deleteWorkOperationRow: (key) => dispatch(deleteLineItemAction(key)),
    patchBooking: (patch) => dispatch(patchBookingAction(patch)),
    addDraftWo: (wo) => dispatch(addDraftWo(wo)),
    sendJobInvite: (invitationId, note) => dispatch(sendJobInvite(invitationId, note)),
    sendQuoteToCustomer: () => dispatch(sendQuoteToCustomerAction()),
    updateToCustomerReview: () => dispatch(updateToCustomerReviewAction()),
    openPopup: (invite) =>
      invite && (!invite.status || invite.status === 'accepted')
        ? dispatch(openPopupAction())
        : message.error(
            `You cannot add a campaign code when the invitation is ${
              !invite.status ? 'draft' : invite.status
            }.`
          ),
  };
}

const mapStateToProps = createStructuredSelector({
  editingKey: editingKeySelector(),
  getGarageBillStatus: garageBillStatusSelector(),
  booking: getBookingSelector(),
  quote: quoteSelector(),
  approvedQuoteItems: approvedQuoteSelector(),
  approvedQuoteTotals: approvedQuoteTotalsSelector(),
  proposedQuoteTotals: proposedQuoteTotalsSelector(),
  proposedQuoteItems: proposedQuoteSelector(),
  garage: garageSelector(),
  garagePrice: getSelectedGaragePrice(),
  garageWorkOperationPrices: garageWorkOperationPricesSelector(),
  originalWorkOperationPrices: originalWorkOperationPricesSelector(),
  jobInvitation: jobInvitationSelector(),
  savingKey: savingKeySelector(),
  deletingKey: deletingKeySelector(),
  workOperations: quoteWorkOperationsSelector(),
  collectionDeliveryLine: collectionDeliverySelector(),
  adjustmentOperation: adjustmentSelector(),
  feeOperation: feeSelector(),
  campaignList: campaignSelector(),
  additionalDiscounts: additionalDiscountsSelector(),
  creditList: creditSelector(),
  hasFinalisedProposedQuote: hasFinalisedProposedQuoteSelector(),
  vehicle: getVehicle(),
  sendQuoteLoading: getSendQuoteLoading(),
});

const withReducer = injectReducer({ key: 'workDetails', reducer });
const withSaga = injectSaga({ key: 'workDetails', saga });

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withSaga, withReducer, withConnect)(WorkDetails);
